import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  fetchLogDates,
  fetchLogSummary,
  fetchHipaaLogs,
  fetchEmailLogs,
  searchLogs,
} from '../../../store/thunks/adminLogsThunk';
import {
  setActiveTab,
  setSelectedDate,
  setSearchMode,
  updateFilters,
  updateSearchParams,
  clearLogs,
  clearError,
} from '../../../store/slices/adminLogsSlice';

const AdminLogsViewer = () => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  
  // Redux state
  const {
    availableDates,
    logs,
    summary,
    status,
    error,
    activeTab,
    selectedDate,
    isSearchMode,
    filters,
    searchParams,
    pagination
  } = useSelector((state) => state.adminLogs);

  // Check if user has access (owner or hr role)
  const hasLogAccess = () => {
    if (!user || !user.role) return false;
    return Array.isArray(user.role) 
      ? user.role.includes('owner') || user.role.includes('hr')
      : user.role === 'owner' || user.role === 'hr';
  };

  // Fetch available log dates
  useEffect(() => {
    if (!hasLogAccess()) return;
    dispatch(fetchLogDates());
  }, [dispatch, user?.role]);

  // Fetch log summary
  useEffect(() => {
    if (!hasLogAccess() || !selectedDate) return;
    dispatch(fetchLogSummary({ date: selectedDate }));
  }, [dispatch, selectedDate, user?.role]);

  // Fetch logs for selected date and tab
  useEffect(() => {
    if (!hasLogAccess() || !selectedDate || isSearchMode) return;
    
    if (activeTab === 'hipaa') {
      dispatch(fetchHipaaLogs({ date: selectedDate, filters }));
    } else {
      dispatch(fetchEmailLogs({ date: selectedDate, filters }));
    }
  }, [dispatch, activeTab, selectedDate, filters, user?.role, isSearchMode]);

  const handleSearch = () => {
    if (!searchParams.searchTerm && !searchParams.eventType) {
      return; // Let the Redux thunk handle the error
    }

    dispatch(searchLogs({
      logType: activeTab,
      ...searchParams,
      limit: filters.limit
    }));
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString();
  };

  const getEventTypeColor = (eventType) => {
    const colors = {
      // HTTP Actions
      'HTTP_GET': 'text-blue-600',
      'HTTP_POST': 'text-green-600',
      'HTTP_PUT': 'text-yellow-600',
      'HTTP_DELETE': 'text-red-600',
      // HTTP Errors
      'HTTP_ERROR_401': 'text-red-600',
      'HTTP_ERROR_403': 'text-red-700',
      'HTTP_ERROR_404': 'text-orange-600',
      'HTTP_ERROR_500': 'text-red-800',
      // Authentication Events
      'LOGIN_SUCCESS': 'text-green-600',
      'LOGIN_FAILED': 'text-red-600',
      'LOGOUT': 'text-blue-600',
      'SYSTEM_LOGIN': 'text-green-600',
      'SYSTEM_LOGOUT': 'text-blue-600',
      // Email Events
      'EMAIL_SENT': 'text-blue-600',
      'EMAIL_DELIVERED': 'text-green-600',
      'EMAIL_BOUNCED': 'text-red-600',
      'EMAIL_OPENED': 'text-purple-600',
      // Security Events
      'SECURITY_EVENT': 'text-orange-600',
      'EMERGENCY_ACCESS': 'text-red-700'
    };
    return colors[eventType] || 'text-gray-600';
  };

  const getActionDescription = (log) => {
    const { eventType, resourceType, emailType, clientNameDecrypted, serviceNameDecrypted } = log;
    
    const descriptions = {
      // HTTP Actions
      'HTTP_GET': 'Viewed',
      'HTTP_POST': 'Created/Updated',
      'HTTP_PUT': 'Updated',
      'HTTP_DELETE': 'Deleted',
      // HTTP Errors
      'HTTP_ERROR_401': 'Unauthorized access to',
      'HTTP_ERROR_403': 'Forbidden access to',
      'HTTP_ERROR_404': 'Attempted to access missing',
      'HTTP_ERROR_500': 'Server error while accessing',
      // Authentication Events
      'LOGIN_SUCCESS': 'Logged into',
      'LOGIN_FAILED': 'Failed to log into',
      'LOGOUT': 'Logged out of',
      'SYSTEM_LOGIN': 'Logged into',
      'SYSTEM_LOGOUT': 'Logged out of',
      // Email Events - Enhanced with context
      'EMAIL_SENT': 'Sent email',
      'EMAIL_DELIVERED': 'Email delivered',
      'EMAIL_BOUNCED': 'Email delivery failed',
      'EMAIL_FAILED': 'Email delivery failed',
      'EMAIL_OPENED': 'Email opened by client',
      // Security Events
      'SECURITY_EVENT': 'Security event in',
      'EMERGENCY_ACCESS': 'Emergency access to',
      'AUTHORIZED_LOG_ACCESS': 'Accessed logs in'
    };
    
    let action = descriptions[eventType] || 'Performed action on';
    
    // For email events, add specific context
    if (eventType && eventType.startsWith('EMAIL_')) {
      if (emailType === 'appointment_reminder') {
        action += ' (Appointment Reminder)';
      } else if (emailType === 'invoice') {
        action += ' (Invoice)';
      } else if (emailType === 'general') {
        action += ' (General Communication)';
      }
      
      // Add client context if available
      if (clientNameDecrypted) {
        action += ` to ${clientNameDecrypted}`;
      }
      
      // Add service context if available
      if (serviceNameDecrypted) {
        action += ` for ${serviceNameDecrypted}`;
      }
      
      return action;
    }
    
    // For non-email events, use original logic
    const resource = resourceType ? resourceType.toLowerCase().replace(/_/g, ' ') : 'system';
    return `${action} ${resource}`;
  };

  if (!hasLogAccess()) {
    return (
      <div className="bg-white shadow-sm rounded-lg p-6">
        <div className="text-center">
          <div className="text-red-600 text-lg font-semibold mb-2">Access Denied</div>
          <p className="text-gray-600">
            Admin log access is restricted to users with 'owner' or 'hr' roles.
          </p>
          <p className="text-sm text-gray-500 mt-2">
            Your current role: {Array.isArray(user?.role) ? user.role.join(', ') : user?.role || 'Unknown'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white shadow-sm rounded-lg p-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Admin Logs Viewer</h1>
        <p className="text-gray-600">
          View and search HIPAA audit logs and email activity logs. Access restricted to owner and HR roles.
        </p>
      </div>

      {/* Log Summary Dashboard */}
      {summary && (
        <div className="bg-white shadow-sm rounded-lg p-6">
          <h2 className="text-lg font-semibold mb-4">Daily Summary - {selectedDate}</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-blue-50 p-4 rounded-lg">
              <div className="text-2xl font-bold text-blue-600">{summary.hipaaAudit.totalEvents}</div>
              <div className="text-sm text-gray-600">HIPAA Events</div>
            </div>
            <div className="bg-red-50 p-4 rounded-lg">
              <div className="text-2xl font-bold text-red-600">{summary.hipaaAudit.securityEvents}</div>
              <div className="text-sm text-gray-600">Security Events</div>
            </div>
            <div className="bg-green-50 p-4 rounded-lg">
              <div className="text-2xl font-bold text-green-600">{summary.emailActivity.emailsDelivered}</div>
              <div className="text-sm text-gray-600">Emails Delivered</div>
            </div>
            <div className="bg-orange-50 p-4 rounded-lg">
              <div className="text-2xl font-bold text-orange-600">{summary.emailActivity.emailsBounced}</div>
              <div className="text-sm text-gray-600">Emails Bounced</div>
            </div>
          </div>
        </div>
      )}

      {/* Controls */}
      <div className="bg-white shadow-sm rounded-lg p-6">
        {/* Tab Selection */}
        <div className="flex space-x-1 mb-6">
          <button
            onClick={() => dispatch(setActiveTab('hipaa'))}
            className={`px-4 py-2 rounded-lg font-medium ${
              activeTab === 'hipaa'
                ? 'bg-blue-100 text-blue-700 border border-blue-300'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            HIPAA Audit Logs
          </button>
          <button
            onClick={() => dispatch(setActiveTab('email'))}
            className={`px-4 py-2 rounded-lg font-medium ${
              activeTab === 'email'
                ? 'bg-blue-100 text-blue-700 border border-blue-300'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Email Logs
          </button>
        </div>

        {/* Mode Toggle */}
        <div className="flex space-x-4 mb-4">
          <button
            onClick={() => dispatch(setSearchMode(false))}
            className={`px-3 py-1 rounded ${
              !isSearchMode ? 'bg-gray-200 text-gray-800' : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            Date View
          </button>
          <button
            onClick={() => dispatch(setSearchMode(true))}
            className={`px-3 py-1 rounded ${
              isSearchMode ? 'bg-gray-200 text-gray-800' : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            Search Mode
          </button>
        </div>

        {!isSearchMode ? (
          /* Date View Controls */
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Date Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
              <select
                value={selectedDate}
                onChange={(e) => dispatch(setSelectedDate(e.target.value))}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
              >
                {(activeTab === 'hipaa' ? availableDates.hipaaLogs || [] : availableDates.emailLogs || []).map(date => (
                  <option key={date} value={date}>{date}</option>
                ))}
              </select>
            </div>

            {/* Event Type Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Event Type</label>
              <select
                value={filters.eventType}
                onChange={(e) => dispatch(updateFilters({ eventType: e.target.value }))}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
              >
                <option value="">All Events</option>
                {activeTab === 'hipaa' ? (
                  <>
                    <option value="HTTP_GET">HTTP GET</option>
                    <option value="HTTP_POST">HTTP POST</option>
                    <option value="HTTP_PUT">HTTP PUT</option>
                    <option value="HTTP_DELETE">HTTP DELETE</option>
                    <option value="HTTP_ERROR_401">HTTP 401 Error</option>
                    <option value="HTTP_ERROR_403">HTTP 403 Error</option>
                    <option value="HTTP_ERROR_404">HTTP 404 Error</option>
                    <option value="HTTP_ERROR_500">HTTP 500 Error</option>
                    <option value="LOGIN_SUCCESS">Login Success</option>
                    <option value="LOGIN_FAILED">Login Failed</option>
                    <option value="LOGOUT">Logout</option>
                  </>
                ) : (
                  <>
                    <option value="EMAIL_SENT">Email Sent</option>
                    <option value="EMAIL_DELIVERED">Email Delivered</option>
                    <option value="EMAIL_BOUNCED">Email Bounced</option>
                    <option value="EMAIL_OPENED">Email Opened</option>
                    <option value="EMAIL_CLICKED">Email Clicked</option>
                  </>
                )}
              </select>
            </div>

            {/* Additional Filters */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {activeTab === 'hipaa' ? 'Severity' : 'Email Type'}
              </label>
              {activeTab === 'hipaa' ? (
                <select
                  value={filters.severity}
                  onChange={(e) => dispatch(updateFilters({ severity: e.target.value }))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                >
                  <option value="">All Severities</option>
                  <option value="LOW">Low</option>
                  <option value="MEDIUM">Medium</option>
                  <option value="HIGH">High</option>
                  <option value="CRITICAL">Critical</option>
                </select>
              ) : (
                <select
                  value={filters.emailType}
                  onChange={(e) => dispatch(updateFilters({ emailType: e.target.value }))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                >
                  <option value="">All Types</option>
                  <option value="appointment_reminder">Appointment Reminder</option>
                  <option value="password_reset">Password Reset</option>
                  <option value="invoice">Invoice</option>
                  <option value="general">General</option>
                </select>
              )}
            </div>
          </div>
        ) : (
          /* Search Mode Controls */
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Search Term</label>
                <input
                  type="text"
                  value={searchParams.searchTerm}
                  onChange={(e) => dispatch(updateSearchParams({ searchTerm: e.target.value }))}
                  placeholder="Search in log content..."
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Event Type</label>
                <select
                  value={searchParams.eventType}
                  onChange={(e) => dispatch(updateSearchParams({ eventType: e.target.value }))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                >
                  <option value="">Any Event Type</option>
                  {activeTab === 'hipaa' ? (
                    <>
                      <option value="HTTP_GET">HTTP GET</option>
                      <option value="HTTP_POST">HTTP POST</option>
                      <option value="HTTP_ERROR_401">HTTP 401 Error</option>
                      <option value="LOGIN_SUCCESS">Login Success</option>
                      <option value="LOGIN_FAILED">Login Failed</option>
                    </>
                  ) : (
                    <>
                      <option value="EMAIL_SENT">Email Sent</option>
                      <option value="EMAIL_DELIVERED">Email Delivered</option>
                      <option value="EMAIL_BOUNCED">Email Bounced</option>
                    </>
                  )}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                <input
                  type="date"
                  value={searchParams.dateRange.start}
                  onChange={(e) => dispatch(updateSearchParams({ 
                    dateRange: {...searchParams.dateRange, start: e.target.value}
                  }))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                <input
                  type="date"
                  value={searchParams.dateRange.end}
                  onChange={(e) => dispatch(updateSearchParams({ 
                    dateRange: {...searchParams.dateRange, end: e.target.value}
                  }))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                />
              </div>
            </div>
            <button
              onClick={handleSearch}
              disabled={status === 'loading'}
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {status === 'loading' ? 'Searching...' : 'Search Logs'}
            </button>
          </div>
        )}
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="text-red-600 font-medium">Error</div>
          <div className="text-red-700">{error}</div>
        </div>
      )}

      {/* Logs Display */}
      <div className="bg-white shadow-sm rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium">
            {activeTab === 'hipaa' ? 'HIPAA Audit Logs' : 'Email Activity Logs'}
            {!isSearchMode && ` - ${selectedDate}`}
            <span className="text-sm text-gray-500 ml-2">({logs.length} entries)</span>
          </h3>
        </div>
        
        <div className="p-6">
          {status === 'loading' ? (
            <div className="text-center py-8">
              <div className="text-gray-500">Loading logs...</div>
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-8">
              <div className="text-gray-500">No logs found for the selected criteria.</div>
            </div>
          ) : (
            <div className="space-y-4">
              {logs.map((log, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-5 hover:bg-gray-50 transition-colors">
                  {/* Header: User and Action */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        {/* User Avatar */}
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          activeTab === 'email' && (!log.userName && !log.userEmail) 
                            ? 'bg-green-100' 
                            : 'bg-blue-100'
                        }`}>
                          <span className={`font-semibold text-sm ${
                            activeTab === 'email' && (!log.userName && !log.userEmail)
                              ? 'text-green-600'
                              : 'text-blue-600'
                          }`}>
                            {activeTab === 'email' && (!log.userName && !log.userEmail)
                              ? '📧'
                              : (log.userName || log.userEmail || 'Unknown').substring(0, 2).toUpperCase()
                            }
                          </span>
                        </div>
                        
                        {/* User Info */}
                        <div>
                          <div className="font-semibold text-gray-900">
                            {activeTab === 'email' && (!log.userName && !log.userEmail)
                              ? 'Email System'
                              : (log.userName || log.userEmail || 'Unknown User')
                            }
                          </div>
                          {log.userName && log.userEmail && (
                            <div className="text-sm text-gray-500">{log.userEmail}</div>
                          )}
                          {activeTab === 'email' && (!log.userName && !log.userEmail) && (
                            <div className="text-sm text-gray-500">SendGrid Webhook</div>
                          )}
                        </div>
                      </div>
                      
                      {/* Action Description */}
                      <div className="ml-13 text-gray-700">
                        <span className="font-medium">
                          {getActionDescription(log)}
                        </span>
                        {log.resourceType && (
                          <span className="ml-1 px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full">
                            {log.resourceType}
                          </span>
                        )}
                        {log.emailType && (
                          <span className="ml-1 px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">
                            {log.emailType.replace('_', ' ').toUpperCase()}
                          </span>
                        )}
                      </div>
                    </div>
                    
                    {/* Timestamp and Status */}
                    <div className="text-right">
                      <div className="text-sm text-gray-500">
                        {formatTimestamp(log.timestamp)}
                      </div>
                      {(log.outcome || log.status) && (
                        <div className={`text-xs px-2 py-1 rounded-full mt-1 ${
                          (log.outcome === 'SUCCESS' || log.status === 'DELIVERED') ? 'bg-green-100 text-green-700' :
                          (log.outcome === 'FAILURE' || log.status === 'FAILED' || log.status === 'BOUNCED') ? 'bg-red-100 text-red-700' :
                          (log.status === 'QUEUED' || log.status === 'SENT') ? 'bg-blue-100 text-blue-700' :
                          'bg-yellow-100 text-yellow-700'
                        }`}>
                          {log.outcome || log.status}
                        </div>
                      )}
                      {isSearchMode && log.logDate && (
                        <div className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded mt-1">
                          {log.logDate}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Email Details (PHI) - Enhanced for Email Logs */}
                  {activeTab === 'email' && (log.clientNameDecrypted || log.toEmailDecrypted || log.appointmentDateDecrypted || log.serviceNameDecrypted || log.toEmail || log.fromEmail || log.subject) && (
                    <div className="ml-13 bg-blue-50 border-l-4 border-blue-200 p-3 rounded-r">
                      <div className="text-xs text-blue-800 font-medium mb-2">
                        📧 Email Communication Details (PHI Protected)
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {/* Client Information */}
                        {(log.clientNameDecrypted || log.toEmailDecrypted || log.toEmail || log.fromEmail) && (
                          <div>
                            <div className="text-xs font-medium text-gray-600 mb-1">Client Information</div>
                            {log.clientNameDecrypted && (
                              <div className="text-sm text-gray-700">
                                <span className="font-medium">Name:</span> {log.clientNameDecrypted}
                              </div>
                            )}
                            {log.toEmailDecrypted && (
                              <div className="text-sm text-gray-700">
                                <span className="font-medium">Email:</span> {log.toEmailDecrypted}
                              </div>
                            )}
                            {!log.toEmailDecrypted && log.toEmail && (
                              <div className="text-sm text-gray-700">
                                <span className="font-medium">To Email:</span> 
                                <span className="ml-1 px-2 py-1 bg-gray-200 text-gray-600 text-xs rounded">🔒 Encrypted</span>
                              </div>
                            )}
                            {!log.fromEmailDecrypted && log.fromEmail && (
                              <div className="text-sm text-gray-700">
                                <span className="font-medium">From Email:</span> 
                                <span className="ml-1 px-2 py-1 bg-gray-200 text-gray-600 text-xs rounded">🔒 Encrypted</span>
                              </div>
                            )}
                          </div>
                        )}
                        
                        {/* Appointment Information */}
                        {(log.appointmentDateDecrypted || log.serviceNameDecrypted || log.clinicianNameDecrypted) && (
                          <div>
                            <div className="text-xs font-medium text-gray-600 mb-1">Appointment Details</div>
                            {log.serviceNameDecrypted && (
                              <div className="text-sm text-gray-700">
                                <span className="font-medium">Service:</span> {log.serviceNameDecrypted}
                              </div>
                            )}
                            {log.appointmentDateDecrypted && (
                              <div className="text-sm text-gray-700">
                                <span className="font-medium">Date:</span> {new Date(log.appointmentDateDecrypted).toLocaleDateString()}
                              </div>
                            )}
                            {log.clinicianNameDecrypted && (
                              <div className="text-sm text-gray-700">
                                <span className="font-medium">Provider:</span> {log.clinicianNameDecrypted}
                              </div>
                            )}
                          </div>
                        )}
                        
                        {/* Invoice Information */}
                        {(log.invoiceAmountDecrypted || log.subject) && (
                          <div>
                            <div className="text-xs font-medium text-gray-600 mb-1">Invoice Details</div>
                            {log.invoiceAmountDecrypted && (
                              <div className="text-sm text-gray-700">
                                <span className="font-medium">Amount:</span> ${log.invoiceAmountDecrypted}
                              </div>
                            )}
                            {log.subjectDecrypted && (
                              <div className="text-sm text-gray-700">
                                <span className="font-medium">Subject:</span> {log.subjectDecrypted}
                              </div>
                            )}
                            {!log.subjectDecrypted && log.subject && (
                              <div className="text-sm text-gray-700">
                                <span className="font-medium">Subject:</span> 
                                <span className="ml-1 px-2 py-1 bg-gray-200 text-gray-600 text-xs rounded">🔒 Encrypted</span>
                              </div>
                            )}
                          </div>
                        )}
                        
                        {/* Engagement Information */}
                        {(log.openedFromIpDecrypted || log.openedFromUserAgentDecrypted) && (
                          <div>
                            <div className="text-xs font-medium text-gray-600 mb-1">Engagement Details</div>
                            {log.openedFromIpDecrypted && (
                              <div className="text-sm text-gray-700">
                                <span className="font-medium">Opened from IP:</span> {log.openedFromIpDecrypted}
                              </div>
                            )}
                            {log.openedFromUserAgentDecrypted && (
                              <div className="text-sm text-gray-700 truncate">
                                <span className="font-medium">Device:</span> {log.openedFromUserAgentDecrypted}
                              </div>
                            )}
                            {!log.openedFromUserAgentDecrypted && log.userAgent && (
                              <div className="text-sm text-gray-700">
                                <span className="font-medium">User Agent:</span> 
                                <span className="ml-1 px-2 py-1 bg-gray-200 text-gray-600 text-xs rounded">🔒 Encrypted</span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      
                      {/* Failure Information */}
                      {(log.failureReason || log.bounceClassification) && (
                        <div className="mt-3 pt-3 border-t border-blue-200">
                          <div className="text-xs font-medium text-red-600 mb-1">Failure Information</div>
                          {log.failureReason && (
                            <div className="text-sm text-red-700">
                              <span className="font-medium">Reason:</span> {log.failureReason}
                            </div>
                          )}
                          {log.bounceClassification && (
                            <div className="text-sm text-red-700">
                              <span className="font-medium">Classification:</span> {log.bounceClassification}
                            </div>
                          )}
                          {log.requiresFollowUp && (
                            <div className="text-sm text-red-700">
                              <span className="font-medium">Action Required:</span> Follow-up needed
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* HIPAA Resource Information (PHI) - For non-email logs */}
                  {activeTab === 'hipaa' && (log.resourceNameDecrypted || log.resourceIdDecrypted || log.detailsDecrypted) && (
                    <div className="ml-13 bg-yellow-50 border-l-4 border-yellow-200 p-3 rounded-r">
                      <div className="text-xs text-yellow-800 font-medium mb-1">
                        🔒 Protected Health Information (PHI)
                      </div>
                      {log.resourceNameDecrypted && (
                        <div className="text-sm text-gray-700">
                          <span className="font-medium">Resource:</span> {log.resourceNameDecrypted}
                        </div>
                      )}
                      {log.resourceIdDecrypted && (
                        <div className="text-sm text-gray-700">
                          <span className="font-medium">ID:</span> {log.resourceIdDecrypted}
                        </div>
                      )}
                      {log.detailsDecrypted && (
                        <div className="text-sm text-gray-700">
                          <span className="font-medium">Details:</span> {log.detailsDecrypted}
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Technical Details */}
                  <div className="ml-13 mt-3 flex flex-wrap gap-4 text-xs text-gray-500">
                    {log.ipAddressDecrypted && (
                      <div>
                        <span className="font-medium">IP:</span> {log.ipAddressDecrypted}
                      </div>
                    )}
                    {!log.ipAddressDecrypted && log.ipAddress && (
                      <div>
                        <span className="font-medium">IP:</span> 
                        <span className="ml-1 px-1 py-0.5 bg-gray-200 text-gray-600 rounded">🔒</span>
                      </div>
                    )}
                    {log.severity && (
                      <div>
                        <span className="font-medium">Severity:</span> 
                        <span className={`ml-1 px-2 py-1 rounded ${
                          log.severity === 'HIGH' || log.severity === 'CRITICAL' ? 'bg-red-100 text-red-700' :
                          log.severity === 'MEDIUM' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-green-100 text-green-700'
                        }`}>
                          {log.severity}
                        </span>
                      </div>
                    )}
                    {log.duration && (
                      <div>
                        <span className="font-medium">Duration:</span> {log.duration}ms
                      </div>
                    )}
                    {log.affectedRecords && (
                      <div>
                        <span className="font-medium">Records:</span> {log.affectedRecords}
                      </div>
                    )}
                    
                    {/* Email-specific technical details */}
                    {activeTab === 'email' && (
                      <>
                        {(log.sgMessageId || log.emailId) && (
                          <div>
                            <span className="font-medium">Message ID:</span> {(log.sgMessageId || log.emailId || '').split('.')[0]}
                          </div>
                        )}
                        {log.processingDelay && (
                          <div>
                            <span className="font-medium">Processing Delay:</span> {Math.round(log.processingDelay)}s
                          </div>
                        )}
                        {log.engagementDelay && (
                          <div>
                            <span className="font-medium">Engagement Delay:</span> {Math.round(log.engagementDelay)}s
                          </div>
                        )}
                        {log.attemptNumber && log.attemptNumber > 1 && (
                          <div>
                            <span className="font-medium">Attempt:</span> {log.attemptNumber}
                          </div>
                        )}
                        {log.category && (
                          <div>
                            <span className="font-medium">Category:</span> 
                            <span className="ml-1 px-2 py-1 bg-purple-100 text-purple-700 rounded">
                              {log.category.replace('_', ' ')}
                            </span>
                          </div>
                        )}
                      </>
                    )}
                    
                    <div>
                      <span className="font-medium">Event:</span> 
                      <span className={`ml-1 ${getEventTypeColor(log.eventType)}`}>
                        {log.eventType}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminLogsViewer;